import type { KyInstance } from 'ky';

import { convertToQueryString, ensureError, isDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { MailingsByDeadlineData } from './types/mailings-by-deadline-data.type';
import type { MailingsByDeadlineQuery } from './types/mailings-by-deadline-query.type';

/**
 * Retrieves the Package Manager board's deadline lanes.
 *
 * @remarks
 * Omit `lane` for the board's initial load, which returns the first `pageSize` rows of every lane in
 * one request. Supply `lane` to page within that one lane, which is what an infinite scroll asks for
 * as the reader nears the end of a lane's loaded rows.
 *
 * The tag surface goes on BOTH calls. A lane key plus a page number looks self-contained, which is
 * exactly why the tags get dropped — and without them, page 2 of a lane returns rows that were never
 * in page 1's set.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The lane, page and tag filters.
 * @param isExternal - Whether to use the external mailings endpoint. Defaults to `false`.
 * @returns A promise resolving to an operation result containing the lane totals and rows.
 * @throws Throws an error if the request fails.
 */
export const getMailingsByDeadline = async (
  instance: KyInstance,
  query: MailingsByDeadlineQuery,
  isExternal = false
) => {
  try {
    const { isClosed = false, lane, sort, ...searchParams } = query;

    return await instance
      .get<OperationResultWithData<MailingsByDeadlineData>>(
        isExternal ? Uri.mailing.getExternalMailingsByDeadline : Uri.mailing.getMailingsByDeadline,
        {
          searchParams: {
            ...searchParams,
            isClosed,
            // Omitted entirely rather than sent as an empty value: the server reads an absent `lane`
            // as "every lane", and a blank one would bind as a lane that does not exist.
            ...(isDefined(lane) ? { lane } : {}),
            ...convertToQueryString(sort, 'sort'),
          },
        }
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
